/*
 * Ryan O'Dowd
 * 2025-05-26
 * © Copyright 2025 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import globals from '@utils/globals';
import useTypedSelector from './useTypedSelector';

const _sorts = {
  chronological: (w1, w2, groupByTranslation) => {
    if (groupByTranslation) {
      return w1.translation_id - w2.translation_id || w1.verse_id - w2.verse_id;
    }

    return w1.verse_id - w2.verse_id;
  },
  srsLevel: (w1, w2, groupByTranslation) => {
    if (groupByTranslation) {
      return w1.translation_id - w2.translation_id || w1.level_number - w2.level_number;
    }

    return w1.level_number - w2.level_number;
  },
  dateLastLeveledUp: (w1, w2, groupByTranslation) => {
    if (groupByTranslation) {
      return w1.translation_id - w2.translation_id || new Date(w1.date_last_passed).getTime() - new Date(w2.date_last_passed).getTime();
    }

    return new Date(w1.date_last_passed).getTime() - new Date(w2.date_last_passed).getTime();
  },
  dateOfNextLevelUp: (w1, w2, groupByTranslation) => {
    if (groupByTranslation) {
      return w1.translation_id - w2.translation_id || new Date(w1.date_next_srs_level).getTime() - new Date(w2.date_next_srs_level).getTime();
    }

    return new Date(w1.date_next_srs_level).getTime() - new Date(w2.date_next_srs_level).getTime();
  },
};

export default function useGetWrinkles(srsLevelThreshold = null, excludeWaiting = false, excludePaused = true, sortMethod = 'chronological', groupByTranslation = true, groupAdjacentVerses = true, showInProgressFirst = false) { // @TODO: this might not be performant
  return useTypedSelector((state) => {
    const completed = [];
    const inProgress = [];
    Object.values(state.wrinkles).forEach((wrinkleVariants: any) => {
      wrinkleVariants.forEach((wv) => {
        if ((!excludePaused || !wv.is_paused) && ((!srsLevelThreshold && !excludeWaiting) || ((!srsLevelThreshold || wv.level_number <= srsLevelThreshold) && (!excludeWaiting || (new Date(wv.date_next_srs_level).getTime() <= Date.now()))))) { // @TODO: split these out into temp variables for easier reading
          const wrinkleVariant = {...wv};

          let arrayOverride;
          if (srsLevelThreshold || !showInProgressFirst) {
            // return only one list with in-progress and complete combined
            arrayOverride = inProgress;
          }

          if (wrinkleVariant.level_number >= globals.srsLevelWrinkleIsEarnedAt) {
            (arrayOverride || completed).push(wrinkleVariant);
          } else if (wrinkleVariant.level_number > 0) {
            (arrayOverride || inProgress).push(wrinkleVariant);
          }
        }
      });
    });

    if (!_sorts[sortMethod]) {
      sortMethod = 'chronological';
    }
    completed.sort((w1, w2) => _sorts[sortMethod](w1, w2, groupByTranslation));
    inProgress.sort((w1, w2) => _sorts[sortMethod](w1, w2, groupByTranslation));

    const _getBookAndChatperFromApiId = (verseApiId) => {
      return verseApiId.split('.').slice(0, 2).join('.');
    };

    const _groupWrinkles = (wrinkles) => {
      const grouped = [];

      let currSequence = [];
      let prevWrinkle;
      for (let i = 0; i < wrinkles.length; i++) {
        const currWrinkle = wrinkles[i];

        const firstInSequence = !currSequence.length;
        const verseIsAdjacent = (prevWrinkle?.verse_id === currWrinkle.verse_id - 1) && (prevWrinkle.translation_id === currWrinkle.translation_id && _getBookAndChatperFromApiId(prevWrinkle.verse_api_id) === _getBookAndChatperFromApiId(currWrinkle.verse_api_id));
        if (firstInSequence || (groupAdjacentVerses && verseIsAdjacent)) {
          // this is a sequential verse in the same translation, book, and chapter as the previous wrinkle (or it's the first verse in the sequence)
          currSequence.push(currWrinkle);
        } else {
          // this is a new passage...not a sequential verse
          grouped.push(currSequence);
          currSequence = [currWrinkle];
        }

        prevWrinkle = currWrinkle;
      }
      if (currSequence.length) {
        grouped.push(currSequence);
      }

      return grouped;
    };

    return [_groupWrinkles(completed), _groupWrinkles(inProgress)];
  }, (oldValue, newValue) => {
    return JSON.stringify(oldValue) === JSON.stringify(newValue);
    // @TODO: would love to figure out why this doesn't work...oldValue is always out of order: const isEqual = JSON.stringify(oldValue) === JSON.stringify(newValue);
    //const olds = JSON.stringify([
    //  oldValue[0].map((wg) => wg.map((w) => `${w.translation_id}-${w.verse_id}-${w.date_last_passed}-${w.is_paused}`)),
    //  oldValue[1].map((wg) => wg.map((w) => `${w.translation_id}-${w.verse_id}-${w.date_last_passed}-${w.is_paused}`)),
    //]);
    //const news = JSON.stringify([
    //  newValue[0].map((wg) => wg.map((w) => `${w.translation_id}-${w.verse_id}-${w.date_last_passed}-${w.is_paused}`)),
    //  newValue[1].map((wg) => wg.map((w) => `${w.translation_id}-${w.verse_id}-${w.date_last_passed}-${w.is_paused}`)),
    //]);

    //return olds === news;
  });
}
